/**
 * @file: InvestorDashboard.jsx
 * @status: Sovereign Integrated Update (V12.6.1) - COMPACT & STEALTH ACTIVE & ACCESSIBILITY ENHANCED
 * @authority: @CEO - Abdullah Ahmed Al-Zahrani
 * @version: 2026.04.27
 * @description: بوابة المستثمرين المحدثة؛ إضافة id/name للنماذج والأزرار؛ الحفاظ على المحرك السحابي POST والتعميد السيادي بختم @CEO.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { 
  Lightbulb, Box, Truck, Award, Search, 
  TrendingUp, Globe, ShieldCheck, Zap,
  BarChart3, PieChart, Activity, Target,
  ArrowUpRight, Factory, Wallet, Lock, AlertCircle,
  Cpu, Workflow, Landmark, FileText, Database 
} from 'lucide-react';
import { message, Tag, Tooltip, Progress, Button } from 'antd';

import PatentsTable from '../components/PatentsTable';

const InvestorDashboard = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [searchTerm, setSearchTerm] = useState("");

  const userRole = localStorage.getItem('huger_user_role');
  const isAuthenticated = localStorage.getItem('huger_authenticated') === 'true';
  const API_URL = "https://portal.huger.sa/api.php";

  useEffect(() => {
    const allowedRoles = ['CEO', 'Investor', 'Manager'];
    if (!isAuthenticated || !allowedRoles.includes(userRole)) {
      message.error(i18n.language === 'ar' ? "⚠️ خرق أمني: الوصول للبوابة يتطلب رتبة قيادية." : "⚠️ Security Breach: Leadership rank required.");
      navigate('/control-center');
      return;
    }
  }, [isAuthenticated, userRole, navigate, i18n.language]);

  const financialMetrics = {
    industrialLoan: 24280000, 
    targetInvestment: 35000000, 
    actualProductionValue: 12500000, 
    roi: "18.5%",
    localizationRate: 92
  };

  const handleRequestDesign = async (patentName) => {
    if (userRole !== 'CEO') {
      message.error(t('restricted_access') || "Restricted Access");
      return;
    }
    const hide = message.loading(i18n.language === 'ar' ? "جاري إرسال التعميد السيادي..." : "Sending Sovereign Seal...", 0);
    try {
      const response = await axios.post(API_URL, {
        action: 'approve_patent_production',
        patent_name: patentName,
        authorized_by: "@CEO",
        timestamp: new Date().toISOString()
      });
      
      if (response.data && response.data.success) {
        message.success(i18n.language === 'ar' ? `🚀 @CEO_Action: تم تعميد (${patentName}) بنجاح. ✅` : `🚀 @CEO_Action: (${patentName}) Sealed Successfully. ✅`);
      } else {
        throw new Error("Cloud Sync Rejected");
      }
    } catch (err) {
      console.error("Critical: Cloud Engine Error", err);
      message.error(i18n.language === 'ar' ? "عائق تقني في التعميد السحابي." : "Technical error in Cloud Sealing.");
    } finally {
      hide();
    }
  };

  const stats = [
    { id: 1, label: t('impact_patents'), value: '3,142', sub: i18n.language === 'ar' ? 'براءة' : 'Patents', icon: <Lightbulb size={18} className="text-yellow-400" /> },
    { id: 2, label: t('industrial_loan'), value: '24.28M', sub: 'SAR V-SYNC', icon: <Landmark size={18} className="text-emerald-400" /> },
    { id: 3, label: i18n.language === 'ar' ? 'العائد ROI' : 'ROI', value: financialMetrics.roi, sub: i18n.language === 'ar' ? 'نمو' : 'Growth', icon: <TrendingUp size={18} className="text-blue-400" /> },
    { id: 4, label: i18n.language === 'ar' ? 'المتبقي' : 'Remain', value: '10.72M', sub: i18n.language === 'ar' ? 'فرص' : 'Target', icon: <Target size={18} className="text-purple-400" /> },
  ];

  return (
    <div className={`min-h-screen bg-[#050B14] p-4 md:p-6 text-slate-200 font-sans relative overflow-hidden ${i18n.language === 'ar' ? 'text-right' : 'text-left'}`} dir={i18n.language === 'ar' ? 'rtl' : 'ltr'}>
      
      <div className="absolute top-0 left-0 w-full h-[1px] bg-gradient-to-r from-transparent via-[#F57C00]/20 to-transparent opacity-40"></div>

      {/* 1. Header - Compact View */}
      <header className="max-w-6xl mx-auto flex flex-col md:flex-row justify-between items-center mb-6 gap-6 bg-[#0A192F]/60 p-6 rounded-[2.5rem] border border-white/5 backdrop-blur-2xl relative shadow-xl scale-95 origin-top">
        <div className="flex items-center gap-4">
          <div className="bg-black/40 p-3 rounded-2xl border border-[#F57C00]/20 shadow-lg">
            <Zap size={20} className="text-[#F57C00] animate-pulse" />
          </div>
          <div>
            <h1 className="text-xl md:text-2xl font-black italic tracking-tighter text-white uppercase leading-none">HUGER <span className="text-[#F57C00]">INVEST</span></h1>
            <p className="text-slate-500 font-black uppercase tracking-[0.3em] text-[7px] mt-1 opacity-60">Sovereign Intelligence</p>
          </div>
        </div>
        <div className="relative w-full md:w-1/3">
          <Search className={`absolute ${i18n.language === 'ar' ? 'right-4' : 'left-4'} top-1/2 -translate-y-1/2 text-slate-600`} size={16} />
          <input 
            id="investor_search_radar"
            name="investor_query"
            type="text" 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder={i18n.language === 'ar' ? "البحث المالي بختم @CEO..." : "Financial search..."} 
            className={`w-full bg-black/60 border border-white/10 py-2.5 rounded-xl text-xs text-white outline-none focus:border-[#F57C00]/40 transition-all ${i18n.language === 'ar' ? 'pr-12 pl-4' : 'pl-12 pr-4'}`}
            autoComplete="off"
          />
        </div>
      </header>

      <main className="max-w-6xl mx-auto space-y-6 relative z-10 scale-95 origin-top">
        
        {/* 2. Progress - Compact */}
        <div id="production_finance_monitor_card" className="bg-[#0A192F]/40 border border-white/5 p-6 rounded-[2.5rem] relative overflow-hidden shadow-xl">
          <div className="flex justify-between items-center mb-4">
            <div className="flex items-center gap-3 text-blue-400 font-black italic uppercase text-[9px] tracking-widest">
              <Activity size={16} /> {i18n.language === 'ar' ? 'مراقبة التمويل والإنتاج' : 'Production & Finance Monitor'}
            </div>
            <div className="text-white font-black bg-white/5 px-4 py-1 rounded-full border border-white/5 text-xs">
              {((financialMetrics.actualProductionValue / financialMetrics.targetInvestment) * 100).toFixed(1)}% <span className="text-[8px] text-slate-500 uppercase">CAPITAL TARGET</span>
            </div>
          </div>
          <div className="w-full h-3 bg-black/60 rounded-full p-0.5 border border-white/5">
            <div className="h-full bg-gradient-to-r from-[#F57C00] to-emerald-500 rounded-full transition-all duration-[3s]" style={{ width: `${(financialMetrics.actualProductionValue / financialMetrics.targetInvestment) * 100}%` }}></div>
          </div>
        </div>

        {/* 3. Analysis & Message - Compact Split */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div id="allocation_chart_card" className="bg-[#0A192F]/60 border border-emerald-500/10 p-6 rounded-[2.5rem] backdrop-blur-xl shadow-xl">
            <h4 className="text-[8px] font-black text-emerald-400 uppercase mb-6 tracking-widest flex items-center gap-2 italic">
              <Landmark size={16} /> {i18n.language === 'ar' ? 'تخصيص التدفقات' : 'Allocation'}
            </h4>
            <div className="space-y-4">
              {[
                { label: i18n.language === 'ar' ? 'مجمع الطائف' : 'Taif HQ', val: 65, color: '#10b981' },
                { label: i18n.language === 'ar' ? 'سلاسل الإنتاج' : 'Production', val: 25, color: '#3b82f6' },
                { label: i18n.language === 'ar' ? 'التوطين' : 'Locally', val: 10, color: '#F57C00' }
              ].map((item, i) => (
                <div key={i}>
                  <div className="flex justify-between text-[9px] font-black mb-1 uppercase">
                    <span className="text-slate-500">{item.label}</span>
                    <span className="text-white">{item.val}%</span>
                  </div>
                  <Progress percent={item.val} showInfo={false} strokeColor={item.color} trailColor="rgba(255,255,255,0.03)" strokeWidth={3} />
                </div>
              ))}
            </div>
          </div>
          
          <div id="ceo_sovereign_message_card" className="bg-gradient-to-br from-[#0A192F] to-[#050B14] border border-blue-500/20 p-8 rounded-[2.5rem] lg:col-span-2 shadow-xl relative overflow-hidden flex flex-col justify-center">
            <h4 className="text-[8px] font-black text-blue-400 uppercase mb-4 tracking-widest flex items-center gap-2">
              <FileText size={16} /> {i18n.language === 'ar' ? 'رسالة القيادة @CEO' : 'Sovereign Message @CEO'}
            </h4>
            <p className="text-sm md:text-base leading-relaxed text-slate-200 italic font-medium">
              {i18n.language === 'ar' 
                ? `"القرض الصناعي بـ 24.28 مليون ريال هو المحرك لسيادتنا الصناعية وتأسيس المستقبل."`
                : `"The 24.28M SAR industrial loan is the engine of our industrial sovereignty."`}
            </p>
            <div className="mt-6 flex items-center gap-4 border-t border-white/5 pt-4">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-tr from-[#F57C00] to-orange-500 flex items-center justify-center text-[10px] font-black shadow-lg">CEO</div>
              <div>
                <span className="text-sm text-white font-black block italic tracking-tighter">Abdullah Ahmed Al-Zahrani</span>
                <span className="text-[8px] text-[#F57C00] font-black uppercase tracking-widest block opacity-70">Chairman & Founder • HUGER Digital Empire</span>
              </div>
            </div>
          </div>
        </div>

        {/* 4. Stats Grid - Compact */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {stats.map((stat) => (
            <div key={stat.id} id={`stat_card_${stat.id}`} className="bg-[#0A192F]/60 border border-white/5 p-6 rounded-[2rem] hover:border-[#F57C00]/30 transition-all shadow-lg">
              <div className="flex justify-between items-start mb-4">
                <div className="p-2 bg-black/50 rounded-xl border border-white/5">{stat.icon}</div>
                <ArrowUpRight size={14} className="text-slate-700" />
              </div>
              <p className="text-slate-500 text-[8px] font-black uppercase mb-1 italic">{stat.label}</p>
              <h3 className="text-xl font-black text-white italic tracking-tighter mb-1">{stat.value}</h3>
              <span className="text-[7px] text-emerald-400 font-black uppercase tracking-widest">{stat.sub}</span>
            </div>
          ))}
        </div>

        {/* 5. Table/Lock - Compact */}
        {userRole !== 'Investor' ? (
          <div id="patents_radar_table_container" className="w-full bg-[#0A192F]/60 rounded-[2.5rem] border border-white/5 p-2 shadow-2xl overflow-hidden">
              <PatentsTable />
          </div>
        ) : (
          <div id="investor_portfolio_locked" className="w-full bg-black/40 rounded-[2.5rem] border border-white/5 p-10 text-center relative overflow-hidden">
              <Lock className="mx-auto text-slate-800 mb-4" size={30} />
              <h3 className="text-sm font-black text-slate-500 uppercase tracking-widest">Portfolio Sealed (3142+)</h3>
              <p className="text-slate-600 text-[10px] mt-2 italic font-bold">Data sovereignly encrypted by @CEO.</p>
          </div>
        )}

        {/* 6. Innovations Radar - Compact Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 pb-12">
          <div id="innovations_radar_container" className="lg:col-span-2 bg-[#0A192F]/60 border border-white/5 rounded-[2.5rem] p-8 shadow-xl">
            <div className="flex justify-between items-center mb-8">
              <h2 className="text-lg font-black italic flex items-center gap-3 uppercase tracking-tighter text-white">
                <BarChart3 className="text-[#F57C00]" size={24} /> {i18n.language === 'ar' ? 'رادار الابتكارات' : 'Innovations Radar'}
              </h2>
              <Tag color="blue" className="px-3 py-0.5 font-black uppercase rounded-lg border-none text-[8px]">Live Sync</Tag>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                { title: i18n.language === 'ar' ? 'منصة الفيزياء' : 'Physics Platform', id: '3042', status: i18n.language === 'ar' ? '85% تم التصنيع' : '85% Manufactured', icon: <Cpu size={18}/> },
                { title: i18n.language === 'ar' ? 'حقيبة الميكاترونكس' : 'Mechatronics Kit', id: '2988', status: i18n.language === 'ar' ? 'جاهز للتسليم' : 'Ready Delivery', icon: <Workflow size={18}/> }
              ].map((item, idx) => (
                <div key={idx} id={`innovation_item_${item.id}`} className="bg-black/60 border border-white/5 p-6 rounded-[1.5rem] flex flex-col gap-4">
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-blue-500/5 rounded-xl border border-white/5 text-blue-400">{item.icon}</div>
                    <div>
                      <h4 className="font-black text-white text-md tracking-tighter italic leading-none">{item.title}</h4>
                      <p className="text-[7px] text-slate-600 font-black uppercase mt-1">Registry #{item.id}</p>
                    </div>
                  </div>
                  <div className="flex justify-between items-center border-t border-white/5 pt-4">
                    <span className="text-[8px] text-emerald-400 font-black italic uppercase flex items-center gap-1"><div className="w-1 h-1 bg-emerald-500 rounded-full"></div> {item.status}</span>
                    {userRole === 'CEO' && (
                      <button 
                        id={`btn_order_patent_${item.id}`}
                        name="order_action"
                        onClick={() => handleRequestDesign(item.title)} 
                        className="bg-gradient-to-r from-[#F57C00] to-orange-700 text-white px-3 py-1 rounded-lg font-black uppercase text-[7px] italic border-none cursor-pointer"
                      >
                        Order @CEO
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div id="made_in_saudi_hub_card" className="bg-gradient-to-b from-[#F57C00]/10 to-[#0A192F] border border-[#F57C00]/20 rounded-[2.5rem] p-8 flex flex-col justify-between shadow-xl relative overflow-hidden group">
            <div className="relative z-10">
              <Globe className="text-[#F57C00] mb-6 animate-spin-slow" size={32} />
              <h3 className="text-lg font-black mb-4 uppercase italic text-white">MADE IN SAUDI HUB</h3>
              <p className={`text-xs text-slate-400 font-bold italic border-[#F57C00] ${i18n.language === 'ar' ? 'border-r pr-4' : 'border-l pl-4'}`}>
                {i18n.language === 'ar' ? 'تصدير التقنيات التعليمية الموطنة بختم ضخامة.' : 'Exporting technologies localized with HUGER sovereignty.'}
              </p>
            </div>
            <div className="mt-8 pt-6 border-t border-white/5 space-y-4">
              <div className="w-full">
                <div className="flex justify-between items-end mb-1 font-black italic">
                  <span className="text-[8px] uppercase text-slate-500 tracking-widest">{t('localization_rate')}</span>
                  <span className="text-[#F57C00] text-lg tracking-tighter">92%</span>
                </div>
                <div className="w-full h-1.5 bg-black/60 rounded-full border border-white/5 overflow-hidden">
                  <div className="w-[92%] h-full bg-[#F57C00] rounded-full transition-all duration-[3s]"></div>
                </div>
              </div>
              <div className="flex items-center gap-3 pt-2">
                <ShieldCheck className="text-emerald-500" size={20} />
                <div><span className="text-[7px] font-black text-slate-500 uppercase block leading-none">License</span><span className="text-[10px] font-black text-[#F57C00] uppercase tracking-widest">ISIC-4-329082</span></div>
              </div>
            </div>
          </div>
        </div>
      </main>
      
      <footer className="max-w-6xl mx-auto mt-6 pb-12 text-center border-t border-white/5 pt-8 scale-90">
        <p className="text-gray-800 text-[8px] font-black uppercase tracking-[1em] italic">
          HUGER SOVEREIGN ECOSYSTEM • v12.6.1 • 2026 • @CEO AUTHORIZED
        </p>
      </footer>
      
      <style>{`
        .animate-spin-slow { animation: spin 40s linear infinite; }
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
};

export default InvestorDashboard;